import type { MenuItemFragment, MenuItemVariationFragment } from '@/generated/graphql'
import { getCustomerFacingPriceItem } from '@/helpers/customerPricing/customerPricing'
import type { Replace, RequireFields } from '@caterdesk/utils--gm-validation/types'
import { isNotNil, areFieldsNotNil } from '@caterdesk/utils--gm-validation/types'

import { isBefore, isAfter, isEqual, startOfDay } from 'date-fns'

import type { MenuItemOption } from './menu-item-option'
import { createMenuItemOption } from './menu-item-option'

// Which price basis the UI renders: marketplace customers see marked-up
// customer-facing prices; internal admin (and eventually the vendor portal)
// see the vendor's own prices, matching the menu-list cards.
export type PriceAudience = 'customer' | 'vendor'

export type MenuItem = Replace<
  RequireFields<
    Omit<MenuItemFragment, 'variations'> & Omit<MenuItemVariationFragment, '__typename'>,
    'isActive' | 'name' | 'price' | 'servings' | 'minQty' | 'priceExTax' | 'taxRate' | 'taxValue'
  >,
  { options: MenuItemOption[] }
> & { variationId: string; vendorPrice: number; vendorPriceExTax?: number | null }

export const filterActiveItems = (item: MenuItemFragment, deliveryDate?: number) => {
  const date = deliveryDate ? new Date(deliveryDate) : startOfDay(new Date())
  const activeFrom = new Date(item.activeFrom as string)
  const activeTo = new Date(item.activeTo as string)
  if (item.activeFrom && isEqual(date, activeFrom)) return true
  if (item.activeTo && isEqual(date, activeTo)) return true
  if (item.activeFrom && item.activeTo) {
    return isAfter(date, activeFrom) && isBefore(date, activeTo)
  }
  if (item.activeFrom) return isAfter(date, activeFrom)
  if (item.activeTo) return isBefore(date, activeTo)
  return true
}

export const createMenuItems = (items: MenuItemFragment[], referenceDate?: Date): MenuItem[] =>
  items
    .filter(isNotNil)
    .filter(areFieldsNotNil(['name', 'price', 'options', 'variations']))
    .map((item) => {
      const { variations, options, ...restItem } = item
      const variation = variations[0]
      if (!variation) return null

      // TODO: change eslint rule
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __typename, _id, ...variationWithoutTypename } = variation

      const customerPriceItem = getCustomerFacingPriceItem(variation.customerPriceItems)

      return {
        ...restItem,
        ...variationWithoutTypename,
        variationId: _id,
        isActive: !!restItem.isActive,
        price: customerPriceItem.price,
        priceExTax: customerPriceItem.priceExTax,
        // Both vendor prices come from the root Pricing section so admin
        // previews match list cards even when variation pricing differs.
        // Keep them separate from customer-facing prices used in basket maths.
        vendorPrice: item.price,
        vendorPriceExTax: item.priceExTax ?? null,
        taxRate: restItem.taxRate,
        taxValue: customerPriceItem.priceTaxValue,
        options: options
          .filter(isNotNil)
          .map((opt) => ({ opt, built: createMenuItemOption(opt, referenceDate) }))
          .filter(({ opt, built }) => opt.items == null || built.items.length > 0)
          .map(({ built }) => built),
        servings: variation.servings || 1,
        minQty: variation.minQty || 1,
        isPrivate: variation.isPrivate || false,
      }
    })
    .filter(isNotNil)
